/**
 * useVideoInfo Hook
 * Updated to match backend API_ROUTES_FINAL.md specification.
 * Custom hook for fetching video information from the API.
 * 
 * All variable names, logs, and comments are in English.
 */

import { useApi } from './useApi';
import { videoInfoApi } from '../api';
import type { VideoInfoResponse } from '../../types/api';

import type { PlatformId } from '../../config/platforms';

interface VideoInfoParams {
  url: string;
  platform: PlatformId;
}

export interface VideoFormat {
  format_id: string;
  quality: string;
  format: string;
  size: number;
  resolution: string;
  fps?: number;
  vcodec?: string;
  acodec?: string;
  protocol?: string;
  language?: string;
  type?: 'video' | 'audio' | 'progressive' | 'image' | 'unknown';
}

export interface VideoInfo {
  videoId: string;
  title: string;
  duration: number;
  thumbnail: string;
  formats: VideoFormat[];
  platform: string;
  originalUrl: string;
  uploadDate: string;
}

export function useVideoInfo() {
  const { loading, error, data, trigger } = useApi<VideoInfoResponse, VideoInfoParams>(
    async ({ url, platform }) => {
      return await videoInfoApi.getVideoInfo(url, platform);
    }
  );

  return {
    loading,
    error,
    videoInfo: data ? {
      videoId: data.videoId,
      title: data.title,
      duration: data.duration,
      thumbnail: data.thumbnail,
      formats: data.formats,
      platform: data.platform,
      originalUrl: data.originalUrl,
      uploadDate: data.uploadDate
    } : undefined,
    fetchVideoInfo: trigger
  };
}

export default useVideoInfo;
